import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { useFetched } from "../../../hooks";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { Wrapper } from "./shared-components/wrapper";
import cn from "classnames";
import {
  CheckoutHeadline,
  CheckoutSubheadline,
} from "../checkout-page-layout/CheckoutComponent/shared/CheckoutHeadline";
import { CustomerOrderStatusResponse, type TranslatedValue } from "@swo/storefront";
import { css } from "@emotion/react";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { useLocation } from "react-router";
import { OrderHeadline } from "./shared-components/order-headline";
import { FONTS, fontSize } from "../../../styles/fonts";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { TickSvg, XIconSvg } from "../../../assets";
import React from "react";
import { mapTranslatedValue, T } from "../../../components/T";

export const OrderStatusPage: ReactTFC<{
  homepageLink: string;
  orderId: string;
  LoadingScreen: React.JSX.Element;
  fetchOrderStatus: () => Promise<CustomerOrderStatusResponse>;
  repeatPayment: {
    link: {
      pathname: string;
      search: string;
    };
    buttonLabel: TranslatedValue;
  };
  translations: {
    orderNotFoundLabel: TranslatedValue;
    orderLabel: TranslatedValue;
    paymentLabel: TranslatedValue;
    paymentStatusLabel: TranslatedValue;
    paymentsLabel: TranslatedValue;
  };
}> = props => {
  const fetchedPaymentOrderStatus = useFetched(
    () => {
      return props.fetchOrderStatus();
    },
    { dataKey: props.orderId },
  );

  const location = useLocation();
  const getPaymentLink = (paymentId: string) => ({
    pathname: location.pathname,
    search: `?${new URLSearchParams({ orderId: props.orderId, paymentId }).toString()}`,
  });

  if (fetchedPaymentOrderStatus.__tag !== "loaded") {
    return <Wrapper className={cn("expand fill-screen")} />;
  }

  const { data } = fetchedPaymentOrderStatus;

  if (data.tag === "not-found") {
    return (
      <Wrapper className={cn("expand fill-screen")}>
        <ContentContainer>
          <div>
            <CheckoutHeadline>
              <T>{props.translations.orderNotFoundLabel}</T>
            </CheckoutHeadline>
          </div>
        </ContentContainer>
      </Wrapper>
    );
  }

  const styles = css`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
  `;

  return (
    <Wrapper className={cn("expand fill-screen")}>
      <ContentContainer>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          {data.isOrderCancelled && <span css={[FONTS.SwarovskiFutura300]}>Cancelled</span>}
          <OrderHeadline orderLabel={props.translations.orderLabel} orderNumber={data.order.orderNumber} />
        </div>

        <div>
          <CheckoutSubheadline>
            <T>{props.translations.paymentsLabel}</T>:
          </CheckoutSubheadline>

          <div
            css={css`
              max-width: 30em;
              margin: 0 auto;
            `}
          >
            {data.payments.map((payment, idx) => {
              const paymentLabel = mapTranslatedValue(props.translations.paymentLabel, _ => `${_} #${idx + 1}`);

              return (
                <div
                  key={payment.id}
                  css={css`
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 32px;
                  `}
                >
                  {!data.isOrderCancelled ? (
                    <SwarovskiButton
                      variant={"plain-underline"}
                      as={"a"}
                      isInternal
                      link={getPaymentLink(payment.id)}
                      size={"small"}
                    >
                      <T>{paymentLabel}</T>
                    </SwarovskiButton>
                  ) : (
                    <SwarovskiButton variant={"plain-underline"} as={"button"} disabled={true} size={"small"}>
                      <T>{paymentLabel}</T>
                    </SwarovskiButton>
                  )}

                  <span>
                    {payment.status === "redirect" && data.canRetryPayment ? <span>Open</span> : null}

                    {(payment.status === "error" || payment.status === "success") && (
                      <div
                        css={css`
                          text-align: center;
                          display: flex;
                          gap: 8px;
                          align-items: center;
                        `}
                      >
                        <div
                          css={css`
                            margin-left: auto;
                            margin-right: auto;

                            width: 36px;
                            height: 36px;

                            svg {
                              width: 50%;
                              height: 50%;
                            }

                            background-color: ${CSS_COLORS.Grey5};
                            color: ${CSS_COLORS.White};

                            &.success {
                              background-color: ${CSS_COLORS.Green};
                              color: ${CSS_COLORS.White};
                            }

                            border-radius: 100%;
                          `}
                          className={cn(payment.status, "u-d-flex u-jc-center u-ai-center")}
                        >
                          {payment.status === "error" && <XIconSvg />}
                          {payment.status === "success" && <TickSvg />}
                        </div>

                        <span css={[fontSize.x2ss, FONTS.SwarovskiFutura300]}>{payment.status}</span>
                      </div>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {data.canRetryPayment && (
          <div
            className={"js-repeat-payment-button"}
            css={[
              styles,
              css`
                margin-top: 40px;
              `,
            ]}
          >
            <SwarovskiButton variant={"green"} as={"a"} isInternal link={props.repeatPayment.link} size={"small"}>
              <T>{props.repeatPayment.buttonLabel}</T>
            </SwarovskiButton>
          </div>
        )}
      </ContentContainer>
    </Wrapper>
  );
};
